import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { Rectangle } from "abweb/utils/geometry/Rectangle";

// TODO: use mobx to make label, width, etc observable
// TODO: Support grouped columns

/**
 * Represents column metadata for a column used in DataGrid component.
 */
export class DataGridColumn {
  constructor(id) {
    _defineProperty(this, "id", void 0);
    _defineProperty(this, "width", void 0);
    /** Used if a specific cell or cells needs to override the computed/measured width **/
    _defineProperty(this, "getWidthOverride", void 0);
    /**
     * Alternative way of representing column width as a percentage of the bodyGrid viewport.
     * Use this instead of width for this case.
     * Should only be used when you have a small number of columns to render.
     */
    _defineProperty(this, "widthPercent", void 0);
    _defineProperty(this, "minWidth", 12);
    _defineProperty(this, "styles", void 0);
    _defineProperty(this, "classNames", void 0);
    /** Additional class names for specific cells **/
    _defineProperty(this, "getClassNames", void 0);
    _defineProperty(this, "cellRenderer", void 0);
    _defineProperty(this, "headerStyles", void 0);
    _defineProperty(this, "headerClassNames", void 0);
    /**
     * If using a cellRenderer this flag will force the grid to still apply the same CSS
     * it applies using the default cell renderer
     */
    _defineProperty(this, "applyDefaultStyles", false);
    /**
     * Optional column header cell renderer that can be used when DataGrid.showColumnHeaders = true.
     * If omitted, column headers are labeled using DataGridColumn.label.
     */
    _defineProperty(this, "headerCellRenderer", void 0);
    /**
     * Optional drag and drop event handlers for the space to the left of the column header.
     * These callbacks are treated as pass-through straight to the divider's HTML element.
     */
    _defineProperty(this, "leftHeaderDividerDragDropEventHandlers", void 0);
    /**
     * Optional drag and drop event handlers for the space between column headers.
     * These callbacks are treated as pass-through straight to the divider's HTML element.
     */
    _defineProperty(this, "headerDividerDragDropEventHandlers", void 0);
    /**
     * Indicates whether this column can be resized using mouse.
     * @type {boolean}
     */
    _defineProperty(this, "allowResize", true);
    /**
     * Allow editing in this column.
     * @type {boolean}
     */
    _defineProperty(this, "editable", true);
    /**
     * Cell renderer to use when editing a cell.
     */
    _defineProperty(this, "editCellRenderer", void 0);
    /** Flag indicating whether any focus loss from the cell editor (e.g. clicking elsewhere in the application)
     * should commit the changes. By default, this is <code>true</code>. If your cell editor is complex and has its own
     * commit handling (e.g. OK/Cancel buttons), set this to <code>false</code>
     */
    _defineProperty(this, "commitOnFocusLoss", true);
    /**
     * Indicates whether this column's header should render the sort buttons
     * @type {boolean}
     */
    _defineProperty(this, "allowSort", true);
    /**
     * Determines if this column is visible or not.
     * Note: You can set this flag before you set the DataGrid columns property, but
     * once the columns are being used in the DataGrid, it is best to use the DataGrid or
     * ColumnVisibilityMachine APIs to change column visibility.
     * @type {boolean}
     */
    _defineProperty(this, "visible", true);
    /**
     * Determines if you can hide this column.
     * Default is true.
     * @type {boolean}
     */
    _defineProperty(this, "canHideColumn", true);
    // Width set by user by changing column width via drag.
    // If set, this overrides width and widthPercent values.
    _defineProperty(this, "userWidth", -1);
    _defineProperty(this, "_childColumns", []);
    _defineProperty(this, "_parentColumn", null);
    if (!id) {
      this.id = `_col${DataGridColumn.nextId++}`;
    } else {
      this.id = id;
    }
  }
  static getLeafColumns(columns, visibleOnly = false) {
    let leafColumns = [];
    for (let n = 0; n < columns.length; n++) {
      processColumn(columns[n]);
    }
    return leafColumns;
    function processColumn(col) {
      if (visibleOnly && !col.visible) {
        // Don't process this column
        return;
      }
      if (col.isLeafColumn) {
        // This is a leaf col
        if (!visibleOnly || col.visible) {
          leafColumns.push(col);
        }
        return;
      }

      // drill into child columns
      for (let childCol of col.childColumns) {
        processColumn(childCol);
      }
    }
  }

  /**
   * Returns all columns in a flattened list.
   * @param {DataGridColumn[]} columns
   * @returns {DataGridColumn[]}
   */
  static getFlattenedColumns(columns) {
    let flattenedColumns = [];
    for (let n = 0; n < columns.length; n++) {
      processColumn(columns[n]);
    }
    return flattenedColumns;
    function processColumn(col) {
      flattenedColumns.push(col);
      if (col.childColumnCount > 0) {
        // drill into child columns
        for (let childCol of col.childColumns) {
          processColumn(childCol);
        }
      }
    }
  }

  /**
   * Gets whether there are columns that can be hidden in the list.
   * @param {DataGridColumn[]} columns
   * @returns {boolean} true if there is at least one leaf column that can be hidden,
   * false otherwise.
   */
  static haveHideableColumns(columns) {
    let leafColumns = DataGridColumn.getLeafColumns(columns);
    return leafColumns.some(col => col.canHideColumn);
  }

  /**
   * Finds a column (or descendant column) by its id.
   * @param {string} id
   * @param {DataGridColumn[]} columns
   * @returns {DataGridColumn | null}
   */
  static findColumnById(id, columns) {
    for (let col of columns) {
      if (col.id === id) {
        return col;
      }
      if (col.childColumns) {
        let childCol = DataGridColumn.findColumnById(id, col.childColumns);
        if (childCol !== null) {
          return childCol;
        }
      }
    }
    return null;
  }
  copyFrom(col) {
    this.label = col.label;
    this.width = col.width;
    this.getWidthOverride = col.getWidthOverride;
    this.widthPercent = col.widthPercent;
    this.minWidth = col.minWidth;
    this.styles = col.styles;
    this.classNames = col.classNames;
    this.getClassNames = col.getClassNames;
    this.cellRenderer = col.cellRenderer;
    this.headerStyles = col.headerStyles;
    this.applyDefaultStyles = col.applyDefaultStyles;
    this.headerCellRenderer = col.headerCellRenderer;
    this.allowResize = col.allowResize;
    this.editable = col.editable;
    this.editCellRenderer = col.editCellRenderer;
    this.allowSort = col.allowSort;
    this.canHideColumn = col.canHideColumn;
    this.visible = col.visible;
  }

  /**
   * Returns evaluated width for this column based on width field.
   * @param {number} colIndex
   * @returns {number}
   */
  getColumnWidth(colIndex) {
    let w;
    if (this.width) {
      if (typeof this.width === "number") {
        w = this.width;
      } else {
        w = this.width({
          index: colIndex
        });
      }
    } else {
      // Return a factory default width
      w = 125;
    }
    return Math.max(this.minWidth, w);
  }
  get childColumns() {
    return this._childColumns;
  }
  set childColumns(value) {
    this._childColumns = value;
    for (let col of value) {
      col._parentColumn = this;
    }
  }
  get childColumnCount() {
    if (this.childColumns) {
      return this.childColumns.length;
    } else {
      return 0;
    }
  }
  get parentColumn() {
    return this._parentColumn;
  }
  clearParent() {
    this._parentColumn = null;
  }

  /**
   * Calculates the depth of this column plus its descendants.
   * Not to be confused with the depth property.
   * @returns {number}
   */
  calculateColumnDepth() {
    if (this.childColumns == null) {
      return 1;
    }
    let childDepth = 0;
    for (let childCol of this.childColumns) {
      childDepth = Math.max(childDepth, childCol.calculateColumnDepth());
    }
    return childDepth + 1;
  }

  /**
   * Gets the depth of this column in its ancestor chain.
   * Top-level columns always have depth 0.
   * @returns {number}
   */
  get depth() {
    if (this.parentColumn == null) {
      return 0;
    }
    return this.parentColumn.depth + 1;
  }

  /**
   * Returns Array of all leaf columns under this grouped column.
   * @returns {DataGridColumn[]}
   */
  getAllLeafColumns(visibleOnly = false) {
    if (visibleOnly && !this.visible) {
      return [];
    }
    let leafColumns = [];
    if (this.isLeafColumn) {
      return this.visible || !visibleOnly ? [this] : [];
    }
    for (let childCol of this.childColumns) {
      leafColumns = leafColumns.concat(childCol.getAllLeafColumns(visibleOnly));
    }
    return leafColumns;
  }
  getVisibleChildColumns() {
    return this.childColumns.filter(col => col.visible);
  }
  get isLeafColumn() {
    return this.childColumnCount === 0;
  }
  get isTopLevelColumn() {
    return this.parentColumn == null;
  }
  get topLevelColumn() {
    let c = this;
    while (!c.isTopLevelColumn) {
      c = c.parentColumn;
    }
    return c;
  }

  /**
   * Gets the cell slot where this column is rendered in the column header Grid.
   * @returns {{rowIndex: number; columnIndex: number; rowSpan: number}}
   */
  get cellSlot() {
    return this._cellSlot;
  }

  /**
   * Returns the Rectangle representing the position of the column header associated with this column.
   * Note that this may return null if the column has not been rendered, or is not in the viewport.
   * To be certain that this is valid, you can use the DataGrid.isCellInViewPort() method to test.
   * @returns {Rectangle | null}
   */
  getColumnHeaderViewPortRect() {
    if (isNaN(this._height) || isNaN(this._top) || isNaN(this._left) || isNaN(this.currentWidth)) {
      return null;
    }
    return new Rectangle(this._left, this._top, this._left + this.currentWidth, this._top + this._height);
  }

  /**
   * Resets the userWidth value which takes precedence if the user drags the column divider
   * to change the width. Once the userWidth gets set, any future calls to width or widthPercent
   * have no effect. Call this method to reset the userWidth override if you would like to change
   * width or widthPercent after the column has been created.
   */
  resetUserWidth() {
    this.userWidth = -1;
  }

  /**
   * Returns the width of the column as set by the user by dragging the column divider.
   * @returns {number} Width of the column as changed by the user by dragging the column divider.
   * If the user has not changed the width, the return value is -1.
   */
  getUserWidth() {
    return this.userWidth;
  }
  get actualWidth() {
    return this.currentWidth;
  }

  /**
   * Gets whether this column has any child columns that are sortable.
   * @returns {boolean}
   */
  haveSortableColumns() {
    if (this.childColumns == null) {
      return false;
    }
    for (let childCol of this.childColumns) {
      if (childCol.allowSort) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns a string that represents an expanded string of label values if the column is inside a grouped or stacked column.
   * If it is not, this property returns the label.
   * @returns {string}
   */
  get expandedName() {
    let ownName = this.label ? this.label : "[ ]";
    return this.parentColumn ? this.parentColumn.expandedName + " > " + ownName : ownName;
  }
}
_defineProperty(DataGridColumn, "nextId", 0);
//# sourceMappingURL=DataGridColumn.js.map
